import BigNumber from 'bignumber.js'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import { TradeDirection } from '~/components/Exchange/types'
import { useDebounce } from '~/hooks'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import { useLimitSwapAmount, useLimitSwapPair } from '~/state/dex/hooks'

const useSpreadColors = () => {
  const theme = useTheme()
  return {
    default: theme.colors.textCaption,
    warning: theme.colors.statusWarning,
    below: theme.colors.textDanger,
    high: theme.colors.statusSuccess,
  }
}

export const Spread = styled.span<{
  color: string
}>`
  color: ${({ color, theme }) => color ?? theme.colors.textCaption};
`
export function getSpreadTextLite(value: string): string {
  if (!value || isNaN(Number(value))) return ''
  const label = Number(value) <= 0 ? '' : '+'
  return `${label}${value}%`
}

export function getSpreadText(value: string): string {
  const label = Number(value) < 0 ? 'spread_down' : 'spread_up'
  return label
}

export const useUSDValueSpreadCallback = () => {
  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const inputPrice = useTokenPrice(inputToken?.address)
  const outputPrice = useTokenPrice(outputToken?.address)
  const debouncedOutputAmount = useDebounce(outputAmount, 300)

  return useCallback(
    (direction: TradeDirection = TradeDirection.Output) => {
      // 暂时只处理 output 的 Spread，后面有需要可以去掉这个条件，把后面计算换成根据 direction 判断
      if (
        direction === TradeDirection.Input ||
        !Number(outputAmount) ||
        !Number(inputAmount)
      )
        return ''

      if (!Number(inputPrice) || !Number(outputPrice)) return '0'

      const usdValue = new BigNumber(outputAmount).times(outputPrice)
      const baseUsdValue = new BigNumber(inputAmount).times(inputPrice)

      return usdValue.div(baseUsdValue).minus(1).times(100).toFixed(2)
    },
    [debouncedOutputAmount, outputPrice],
  )
}

export const useUSDValueSpread = (
  direction: TradeDirection = TradeDirection.Output,
) => {
  const usdValueSpreadCb = useUSDValueSpreadCallback()
  return useMemo(() => {
    return usdValueSpreadCb(direction)
  }, [usdValueSpreadCb, direction])
}

const getSpreadColor = (USDValue: string, colors: ReturnType<typeof useSpreadColors>) => {
  const numUSDValue = Number(USDValue)
  if (numUSDValue > 0) return colors.high
  if (numUSDValue <= -5) return colors.below
  if (numUSDValue <= -1) return colors.warning
  return colors.default
}

export const SpreadLabelLite: React.FC<{
  direction: TradeDirection
}> = ({ direction }) => {
  const colors = useSpreadColors()
  const usdValueSpread = useUSDValueSpread(direction)
  // for 0.00xxx case
  if (Number(usdValueSpread) === 0) return null

  const color = getSpreadColor(usdValueSpread, colors)
  return <Spread color={color}>({getSpreadTextLite(usdValueSpread)})</Spread>
}

export const SpreadLabel: React.FC<{
  direction: TradeDirection
}> = ({ direction }) => {
  const { t } = useTranslation()
  const colors = useSpreadColors()
  const usdValueSpread = useUSDValueSpread(direction)
  const text = t(getSpreadText(usdValueSpread), {
    value: Math.abs(Number(usdValueSpread)),
  })

  if (
    !usdValueSpread ||
    isNaN(Number(usdValueSpread)) ||
    Number(usdValueSpread) === 0
  )
    return null

  const color = getSpreadColor(usdValueSpread, colors)
  return <Spread color={color}>{`(${text})`}</Spread>
}
